import { useEffect, useMemo, useRef } from "react";
import { resolveMedia } from "@/lib/media";
import { cloudinaryDeliveryUrl } from "@/components/HlsVideo";

/**
 * PreloadStack — Cloudinary-first prefetch.
 *
 * Warms the browser cache for the current + next `count` videos so a swipe
 * into a new post paints its first frame instantly. Cloudinary CDN honours
 * HTTP Range requests, so a small Range GET fetches only the initial video
 * container header — enough to let a subsequent <video> element start
 * playing without a long TTFB wait.
 */
const HEADER_BYTES = 512 * 1024;  // 512 KB — enough for MP4/WEBM moov + init segment

async function warmVideoHeader(url, controller) {
  if (!url) return;
  try {
    if (controller.signal.aborted) return;
    await fetch(url, {
      method: "GET",
      cache: "force-cache",
      credentials: "omit",
      headers: { Range: `bytes=0-${HEADER_BYTES - 1}` },
      signal: controller.signal,
    });
  } catch { /* silent */ }
}

function warmImage(url) {
  if (!url) return;
  try {
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = url;
  } catch { /* silent */ }
}

export default function PreloadStack({ items, activeIndex, count = 3 }) {
  const abortRef = useRef(null);

  const targets = useMemo(() => {
    const out = [];
    if (!items || items.length === 0) return out;
    // Warm the CURRENT item + next `count` items.
    for (let i = 0; i <= count; i++) {
      const it = items[activeIndex + i];
      if (!it || it.type !== "post") continue;
      const media = it.data?.hls_url || it.data?.media_url;
      const poster = it.data?.thumbnail_url;
      out.push({
        video: media ? cloudinaryDeliveryUrl(resolveMedia(media)) : null,
        poster: poster ? resolveMedia(poster) : null,
      });
    }
    return out;
  }, [items, activeIndex, count]);

  // Inject a high-priority <link rel="preload" as="video"> into <head> for
  // the top video. Browsers begin the connection + first segment fetch
  // BEFORE the <video> element ever mounts — the video starts painting
  // noticeably sooner. Removed on unmount / target change so the head
  // never accumulates stale hints.
  useEffect(() => {
    const first = targets[0]?.video;
    if (!first) return;
    const existing = document.head.querySelector(`link[data-tain-preload="${first}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "video";
    link.href = first;
    link.setAttribute("fetchpriority", "high");
    link.setAttribute("data-tain-preload", first);
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
    return () => {
      try { document.head.removeChild(link); } catch { /* ignore */ }
    };
  }, [targets]);

  useEffect(() => {
    if (targets.length === 0) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      for (const t of targets) {
        if (controller.signal.aborted) return;
        warmImage(t.poster);
        if (t.video) await warmVideoHeader(t.video, controller);
      }
    })();

    return () => controller.abort();
  }, [targets]);

  return null;
}
